import {baseKeysUseQuery} from '#/hooks/query/base/base-keys-use-query';
import {useInvalidateQueries} from '#/hooks/query/base/use-invalidate-queries';
import {useLands} from '#/hooks/query/use-lands';
import {wagmiConfig} from '#/wagmi/wagmi-config';
import {useQueries, useQuery} from '@tanstack/react-query';
import {Land, ShareLandMarket__factory, marketInfoToLandMarketInfo} from 'shareland-common';
import {readContract, readContracts} from 'wagmi/actions';

export const useAllMarketInfos = () => {
  const {data: lands = []} = useLands();

  return useQuery({
    queryKey: [baseKeysUseQuery.useLandMarketInfo],
    queryFn: async () => {
      const marketInfos = await readContracts(wagmiConfig, {
        contracts: lands.map(
          land =>
            ({
              address: land!.contractAddresses.market,
              abi: ShareLandMarket__factory.abi,
              functionName: 'getMarketInfo',
            }) as const,
        ),
      });

      return marketInfos.map(response => (response.result ? marketInfoToLandMarketInfo(response.result) : undefined));
    },
    initialData: [],
    enabled: !!lands.length,
  });
};

export const useLandMarketInfo = (land: Land | undefined) => {
  return useQuery({
    queryKey: [baseKeysUseQuery.useLandMarketInfo, land?.id],
    queryFn: async () => {
      const marketInfo = await readContract(wagmiConfig, {
        address: land!.contractAddresses.market,
        abi: ShareLandMarket__factory.abi,
        functionName: 'getMarketInfo',
      } as const);

      return marketInfoToLandMarketInfo(marketInfo);
    },
    initialData: land?.marketInfo,
    enabled: !!land,
  });
};

export const useLandMarketInfoQueries = (lands: Land[]) => {
  return useQueries({
    queries: lands.map(land => ({
      queryKey: [baseKeysUseQuery.useLandMarketInfo, land.id],
      queryFn: async () => {
        const marketInfo = await readContract(wagmiConfig, {
          address: land!.contractAddresses.market,
          abi: ShareLandMarket__factory.abi,
          functionName: 'getMarketInfo',
        });

        return marketInfoToLandMarketInfo(marketInfo);
      },
      initialData: land.marketInfo,
    })),
  });
};

export const useLandMarketInfos = (lands: Land[]) => {
  const invalidateQueries = useInvalidateQueries();
  const queryResult = useLandMarketInfoQueries(lands);

  const refetch = () => {
    lands.forEach(({id}) => {
      invalidateQueries.useLandMarketInfos(id);
    });
  };

  return {
    data: queryResult.map(result => result.data),
    isLoading: queryResult.some(result => result.isLoading),
    error: queryResult.find(result => result.error)?.error,
    refetch,
  };
};
